// ════════════════════════════════════════════════════════════════════════════
// components/CRM/ProspectosList.jsx
// Tabla de prospectos con filtros y acciones - VERSIÓN OPTIMIZADA
// ════════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import ProspectoFormModal from '../CRM/modales/ProspectoFormModal';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Chip,
  IconButton,
  Typography,
  Grid,
  CircularProgress,
  Avatar,
  Tooltip,
  Fade,
  Zoom,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  alpha,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Assignment as AssignmentIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Stars as StarsIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import { 
  getProspectos, 
  createProspecto, 
  updateProspecto, 
  deleteProspecto 
} from '../../services/crmService';
import { toast } from 'react-toastify';
import { useFiscal } from '../../hooks/useFiscal';

// ════════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN DE ETIQUETAS Y COLORES - CORREGIDO
// ════════════════════════════════════════════════════════════════════════════

const ESTADOS_CONFIG = {
  nuevo: { label: 'Nuevo', color: '#FF6B6B', icon: '🔥' },
  contactado: { label: 'Contactado', color: '#4ECDC4', icon: '📞' },
  calificado: { label: 'Calificado', color: '#1DD1A1', icon: '✅' },
  no_calificado: { label: 'No Calificado', color: '#FF9F43', icon: '⚠️' },
  archivado: { label: 'Archivado', color: '#576574', icon: '📁' }
};

const FUENTES_CONFIG = {
  web: { label: 'Web', color: '#54A0FF', icon: '🌐' },
  referencia: { label: 'Referencia', color: '#FF9FF3', icon: '👥' },
  feria: { label: 'Feria', color: '#F368E0', icon: '🎪' },
  llamada: { label: 'Llamada', color: '#00D2D3', icon: '📞' },
  visita: { label: 'Visita', color: '#5F27CD', icon: '🏢' },
  redes_sociales: { label: 'Redes', color: '#0ABDE3', icon: '📱' },
  email_marketing: { label: 'Email', color: '#10AC84', icon: '📧' }
};

const TIPOS_PROYECTO_CONFIG = {
  residencial: { label: 'Residencial', color: '#FF9F43' },
  comercial: { label: 'Comercial', color: '#2E86DE' },
  industrial: { label: 'Industrial', color: '#EE5A24' },
  remodelacion: { label: 'Remodelación', color: '#A3CB38' },
  infraestructura: { label: 'Infraestructura', color: '#1289A7' }
};

// ════════════════════════════════════════════════════════════════════════════
// FUNCIONES DE AYUDA PARA ICONOS
// ════════════════════════════════════════════════════════════════════════════

// Componente para mostrar iconos sin duplicación
const IconoConEstilo = ({ icon, size = '1rem' }) => (
  <Box component="span" sx={{ fontSize: size, mr: 1 }}>
    {icon}
  </Box>
);

// ════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL - VERSIÓN CORREGIDA
// ════════════════════════════════════════════════════════════════════════════

const ProspectosList = () => {
  const fiscal = useFiscal();
  const theme = useTheme();
  
  // ════════════════════════════════════════════════════════════════════════
  // STATE
  // ════════════════════════════════════════════════════════════════════════
  
  const [prospectos, setProspectos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    estatus: '',
    fuente: '',
    proyecto_tipo: '',
    search: ''
  });
  
  // Modal de formulario
  const [modalOpen, setModalOpen] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    telefono: '',
    email: '',
    direccion: '',
    rnc_cedula: '',
    fuente: 'llamada',
    proyecto_tipo: 'residencial',
    presupuesto_estimado: '',
  });
  
  // ════════════════════════════════════════════════════════════════════════
  // EFFECTS
  // ════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    cargarProspectos();
  }, [filtros]);
  
  // ════════════════════════════════════════════════════════════════════════
  // FUNCIONES PRINCIPALES
  // ════════════════════════════════════════════════════════════════════════
  
  const cargarProspectos = async () => {
    setLoading(true);
    try {
      const response = await getProspectos(filtros);
      const listaFinal = response.data?.data?.prospectos || [];
      setProspectos(listaFinal);
      console.log("Prospectos cargados:", listaFinal.length);
    } catch (err) {
      console.error('Error:', err);
      setProspectos([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };
  
  // Abrir modal para crear
  const abrirModalCrear = useCallback(() => {
    setEditandoId(null);
    setFormData({
      nombre: '',
      empresa: '',
      telefono: '',
      email: '',
      direccion: '',
      rnc_cedula: '',
      fuente: 'llamada',
      proyecto_tipo: 'residencial',
      presupuesto_estimado: ''
    });
    setModalOpen(true);
  }, []);
  
  // Abrir modal para editar
  const abrirModalEditar = useCallback((prospecto) => {
    setEditandoId(prospecto.id);
    setFormData({
      nombre: prospecto.nombre || '',
      empresa: prospecto.empresa || '',
      telefono: prospecto.telefono || '',
      email: prospecto.email || '',
      direccion: prospecto.direccion || '',
      rnc_cedula: prospecto.rnc_cedula || '',
      fuente: prospecto.fuente || 'llamada',
      proyecto_tipo: prospecto.proyecto_tipo || 'residencial',
      presupuesto_estimado: prospecto.presupuesto_estimado || ''
    });
    setModalOpen(true);
  }, []);
  
  // Cerrar modal
  const cerrarModal = useCallback(() => {
    setModalOpen(false);
    setEditandoId(null);
  }, []);
  
  // Submit del formulario (viene del modal)
  const handleFormSubmit = useCallback(async (datos) => {
    if (!datos.nombre || !datos.fuente || !datos.proyecto_tipo) {
      toast.error('Nombre, fuente y tipo de proyecto son requeridos', { icon: '⚠️' });
      return;
    }
    
    try {
      if (editandoId) {
        await updateProspecto(editandoId, datos);
        toast.success('¡Prospecto actualizado con éxito!', { icon: '✅' });
      } else {
        await createProspecto(datos);
        toast.success('¡Prospecto creado exitosamente!', { icon: '✨' });
      }
      
      cerrarModal();
      cargarProspectos();
    } catch (err) {
      console.error('Error guardando prospecto:', err);
      toast.error(editandoId ? 'Error al actualizar prospecto' : 'Error al crear prospecto');
    }
  }, [editandoId]);
  
  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este prospecto? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      await deleteProspecto(id);
      toast.success('Prospecto eliminado correctamente');
      cargarProspectos();
    } catch (err) {
      console.error('Error eliminando prospecto:', err);
      toast.error('Error al eliminar prospecto');
    }
  };
  
  const handleRefresh = () => {
    cargarProspectos();
    toast.info('Lista actualizada');
  };
  
  // ════════════════════════════════════════════════════════════════════════
  // RENDER - VERSIÓN CORREGIDA
  // ════════════════════════════════════════════════════════════════════════
  
  return (
    <Box sx={{ p: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      {/* Título y botones */}
      <Fade in={true} timeout={800}>
        <Card sx={{ 
          mb: 4, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 56, 
                  height: 56,
                  backdropFilter: 'blur(10px)'
                }}>
                  <TrendingIcon fontSize="large" />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    📋 Lista de Prospectos
                    <Chip 
                      label={`${prospectos.length} registros`}
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.3)', 
                        color: 'white',
                        fontWeight: 'bold',
                        backdropFilter: 'blur(10px)'
                      }}
                    />
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    Gestiona todos tus prospectos de forma eficiente
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Tooltip title="Actualizar lista" arrow>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                      color: 'white',
                      borderRadius: 2,
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.3)'
                    }}
                  >
                    Actualizar
                  </Button>
                </Tooltip>
                
                <Tooltip title="Crear nuevo prospecto" arrow>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={abrirModalCrear}
                    sx={{ 
                      bgcolor: 'white', 
                      color: '#667eea',
                      '&:hover': { 
                        bgcolor: '#f8f9fa',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                      },
                      borderRadius: 2,
                      fontWeight: 'bold',
                      px: 3,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Nuevo Prospecto
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Fade>
      
      {/* Filtros */}
      <Zoom in={true} timeout={600}>
        <Card sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 3,
          bgcolor: 'white',
          boxShadow: '0 5px 25px rgba(0,0,0,0.08)'
        }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2d3436', display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon /> Filtros Avanzados
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar prospectos..."
                value={filtros.search}
                onChange={(e) => handleFiltroChange('search', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filtros.estatus}
                  label="Estado"
                  onChange={(e) => handleFiltroChange('estatus', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Todos los estados</MenuItem>
                  {Object.entries(ESTADOS_CONFIG).map(([value, config]) => (
                    <MenuItem key={value} value={value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconoConEstilo icon={config.icon} />
                        {config.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Fuente</InputLabel>
                <Select
                  value={filtros.fuente}
                  label="Fuente"
                  onChange={(e) => handleFiltroChange('fuente', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Todas las fuentes</MenuItem>
                  {Object.entries(FUENTES_CONFIG).map(([value, config]) => (
                    <MenuItem key={value} value={value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconoConEstilo icon={config.icon} />
                        {config.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de Proyecto</InputLabel>
                <Select
                  value={filtros.proyecto_tipo}
                  label="Tipo de Proyecto"
                  onChange={(e) => handleFiltroChange('proyecto_tipo', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Todos los tipos</MenuItem>
                  {Object.entries(TIPOS_PROYECTO_CONFIG).map(([value, config]) => (
                    <MenuItem key={value} value={value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: '50%', 
                          bgcolor: config.color,
                          mr: 1 
                        }} />
                        {config.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>
      </Zoom>
      
      {/* Tabla de prospectos - CORREGIDO: iconos no duplicados */}
      <Card sx={{ 
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 5px 25px rgba(0,0,0,0.08)'
      }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ 
              bgcolor: 'primary.main',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}>#</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}>Nombre</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}>Empresa</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}>{fiscal.id_fiscal_empresa_label || 'RNC'}/{fiscal.id_fiscal_persona_label || 'Cédula'}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}>Contacto</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}>Fuente</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}>Proyecto</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}>Presupuesto</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}>Estatus</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={60} thickness={4} />
                      <Typography variant="body1" color="text.secondary">
                        Cargando prospectos...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : prospectos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ 
                        bgcolor: '#f8f9fa', 
                        width: 80, 
                        height: 80,
                        border: '3px dashed #dee2e6'
                      }}>
                        <PersonIcon sx={{ fontSize: 40, color: '#adb5bd' }} />
                      </Avatar>
                      <Typography variant="h6" color="text.secondary">
                        {filtros.search ? 'No hay coincidencias' : 'No hay prospectos registrados'}
                      </Typography>
                      {!filtros.search && (
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={abrirModalCrear}
                          sx={{ mt: 2 }}
                        >
                          Crear primer prospecto
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                prospectos.map((prospecto, index) => (
                  <TableRow 
                    key={prospecto.id} 
                    hover
                    sx={{ 
                      '&:hover': { 
                        bgcolor: alpha(theme.palette.primary.light, 0.05),
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s ease'
                      },
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        bgcolor: ESTADOS_CONFIG[prospecto.estatus]?.color || '#667eea',
                        opacity: 0.7
                      }
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight="bold" color="text.secondary">
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ 
                          bgcolor: ESTADOS_CONFIG[prospecto.estatus]?.color || '#667eea',
                          width: 40,
                          height: 40
                        }}>
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {prospecto.nombre}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {prospecto.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon fontSize="small" color="action" />
                        <Typography>
                          {prospecto.empresa || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          color: prospecto.rnc_cedula ? 'text.primary' : 'text.secondary',
                          fontWeight: prospecto.rnc_cedula ? 'bold' : 'normal'
                        }}
                      >
                        {prospecto.rnc_cedula || 'Sin RNC'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{prospecto.telefono || '-'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {prospecto.email || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={FUENTES_CONFIG[prospecto.fuente]?.label || prospecto.fuente}
                        size="small"
                        sx={{ 
                          bgcolor: alpha(FUENTES_CONFIG[prospecto.fuente]?.color || '#667eea', 0.1),
                          color: FUENTES_CONFIG[prospecto.fuente]?.color || '#667eea',
                          fontWeight: 'bold',
                          borderRadius: 1
                        }}
                        icon={FUENTES_CONFIG[prospecto.fuente]?.icon ? 
                          <IconoConEstilo icon={FUENTES_CONFIG[prospecto.fuente].icon} /> : 
                          undefined
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={TIPOS_PROYECTO_CONFIG[prospecto.proyecto_tipo]?.label || prospecto.proyecto_tipo}
                        size="small"
                        sx={{ 
                          bgcolor: alpha(TIPOS_PROYECTO_CONFIG[prospecto.proyecto_tipo]?.color || '#667eea', 0.1),
                          color: TIPOS_PROYECTO_CONFIG[prospecto.proyecto_tipo]?.color || '#667eea',
                          fontWeight: 'bold',
                          borderRadius: 1
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {prospecto.presupuesto_estimado ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MoneyIcon fontSize="small" color="success" />
                          <Typography fontWeight="bold" color="success.main">
                            ${Number(prospecto.presupuesto_estimado).toLocaleString('es-DO')}
                          </Typography>
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={ESTADOS_CONFIG[prospecto.estatus]?.label || prospecto.estatus}
                        size="small"
                        sx={{ 
                          bgcolor: alpha(ESTADOS_CONFIG[prospecto.estatus]?.color || '#667eea', 0.1),
                          color: ESTADOS_CONFIG[prospecto.estatus]?.color || '#667eea',
                          fontWeight: 'bold',
                          borderRadius: 1,
                          border: `1px solid ${alpha(ESTADOS_CONFIG[prospecto.estatus]?.color || '#667eea', 0.3)}`
                        }}
                        icon={ESTADOS_CONFIG[prospecto.estatus]?.icon ? 
                          <IconoConEstilo icon={ESTADOS_CONFIG[prospecto.estatus].icon} /> : 
                          undefined
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Editar prospecto" arrow>
                          <IconButton 
                            size="small" 
                            sx={{ 
                              bgcolor: 'primary.light',
                              color: 'white',
                              '&:hover': { bgcolor: 'primary.main' },
                              transition: 'all 0.3s ease'
                            }}
                            onClick={() => abrirModalEditar(prospecto)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Eliminar prospecto" arrow>
                          <IconButton 
                            size="small" 
                            sx={{ 
                              bgcolor: 'error.light',
                              color: 'white',
                              '&:hover': { bgcolor: 'error.main' },
                              transition: 'all 0.3s ease'
                            }}
                            onClick={() => handleDelete(prospecto.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
{/* Modal de formulario - DIRECCIÓN SIN TÍTULO, PRESUPUESTO ARRIBA */}

      {/* Modal de formulario optimizado */}
      <ProspectoFormModal
        open={modalOpen}
        onClose={cerrarModal}
        onSubmit={handleFormSubmit}
        editandoId={editandoId}
        initialData={formData}
      />
    </Box>
  );
};

export default ProspectosList;